import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from "./ui/dialog";
import { ArrowRight } from "lucide-react";

/**
 * Functional motivation chip — no decorative-only labels.
 *
 * Each chip has a SINGLE PURPOSE:
 *   On click, open a modal with:
 *     - A short reflection / quote
 *     - One clear CTA that routes somewhere useful in the ecosystem
 *
 * Usage:
 *   <MotivationChip label="Transformation"
 *                   quote="..."
 *                   ctaLabel="Take the Dosha Test"
 *                   ctaTo="/dosha" />
 */
export function MotivationChip({ label, quote, ctaLabel, ctaTo, icon: Icon, testid }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="chip inline-flex items-center gap-1.5 text-xs hover:bg-[#C8A24A]/20 hover:border-[#C8A24A]/55 transition-colors"
        data-testid={testid || `motivation-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {Icon && <Icon className="w-3 h-3 gold-text" />}
        {label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md border-white/10 bg-[#0F1116] text-white">
          <DialogHeader>
            <div className="text-[10px] tracking-[0.28em] uppercase gold-text mb-2">{label}</div>
            <DialogTitle className="font-display text-2xl leading-snug">
              {quote}
            </DialogTitle>
            {ctaLabel && (
              <DialogDescription className="text-white/65 text-sm mt-2">
                {t("motivation.cta_hint", "Take the next step:")}
              </DialogDescription>
            )}
          </DialogHeader>
          {ctaLabel && ctaTo && (
            <DialogFooter className="mt-4">
              <Link
                to={ctaTo}
                onClick={() => setOpen(false)}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid={`motivation-cta-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MotivationChip;
